import {IssueRelativeTime} from '@github-ui/ago'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {hovercardAttributes} from '@github-ui/hovercards'
import {IssueStateIcons} from '@github-ui/issue-pr-state/Icons'
import {IssueStatusIcon} from '@github-ui/issue-pr-state/IssueStatusIcon'
import {IssueTypeToken} from '@github-ui/issue-type-token'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {ListItemTrailingBadge} from '@github-ui/list-view/ListItemTrailingBadge'
import {useListViewVariant} from '@github-ui/list-view/ListViewVariantContext'
import {ownerAvatarPath} from '@github-ui/paths'
import {ProfileReference} from '@github-ui/profile-reference'
import {ProgressCircle} from '@github-ui/progress-circle'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {BlockedIcon, CommentIcon, MilestoneIcon, SmileyIcon} from '@primer/octicons-react'
import {Link, Token, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import type {ElementType, KeyboardEvent, MouseEvent, ReactNode} from 'react'

import {AssigneeAvatars} from '../shared/AssigneeAvatars'
import compactStyles from '../shared/CompactListItem.module.css'
import {renderIssueFieldBadges} from '../shared/IssueFieldBadges'
import {renderLabelBadges} from '../shared/LabelBadges'
import metadataLinkStyles from '../shared/MetadataLink.module.css'
import {unreadRowClassName, UnreadRowLabel} from '../shared/UnreadRowIndicator'
import {getReactionSortDisplay} from '../shared/consts/sorting-consts'
import {getListItemIdentity} from '../shared/helpers/item-identity'
import type {FilterSelectType} from '../shared/types/list-types'
import inlineCodeStyles from '../shared/TitleInlineCode.module.css'
import styles from './IssueListItem.module.css'
import type {
  DashboardIssueIdentifier,
  EnrichedIssueListItem,
  IssueItemStatus,
  IssueStateReason,
  IssueStateType,
} from './types/list-types'
import {getIssueReference} from './helpers/issue-reference'
import {LinkedPullRequests} from './LinkedPullRequests'

type IssueReferenceProps = {
  compact?: boolean
  issue: EnrichedIssueListItem
  onFilterSelect?: (type: FilterSelectType, value: string) => void
  showRepository: boolean
}

function getIssueStateIcon(state: IssueStateType, stateReason: IssueStateReason, status?: IssueItemStatus) {
  if (status) {
    return (
      <IssueStatusIcon status={{name: status.name, category: {key: status.category, state}}} aria-label={status.name} />
    )
  }

  if (state === 'OPEN') return <IssueStateIcons.OPEN />
  if (stateReason === 'NOT_PLANNED') return <IssueStateIcons.NOT_PLANNED />
  if (stateReason === 'DUPLICATE') return <IssueStateIcons.DUPLICATE />
  return <IssueStateIcons.COMPLETED />
}

function isOrganizationAccountIssue(permalink: string) {
  return /^(?:https?:\/\/[^/]+)?\/orgs\//.test(permalink)
}

function IssueReference({compact = false, issue, onFilterSelect, showRepository}: IssueReferenceProps) {
  const [ownerLogin = ''] = issue.repoNameWithOwner.split('/')

  const {isAccountIssue} = issue
  const issueReference = getIssueReference({isAccountIssue, issue, ownerLogin, showRepository})
  if (isAccountIssue) {
    return (
      <span className="d-flex flex-items-center">
        <VisuallyHidden>Account issue </VisuallyHidden>
        <GitHubAvatar
          src={ownerAvatarPath({owner: ownerLogin})}
          size={16}
          square={isOrganizationAccountIssue(issue.permalink)}
          alt=""
          className="mr-1"
          data-testid="account-issue-avatar"
        />
        {issueReference}
      </span>
    )
  }

  if (!showRepository) return <span>{issueReference}</span>
  if (!compact) return <span>{issueReference}</span>
  if (!onFilterSelect) return <span>{issue.repoNameWithOwner}</span>

  return (
    <button
      type="button"
      className={styles.filterLink}
      onClick={() => onFilterSelect('repo', issue.repoNameWithOwner)}
      aria-label={`Filter by repository ${issue.repoNameWithOwner}`}
    >
      {issue.repoNameWithOwner}
    </button>
  )
}

/**
 * Meta/Ctrl/Shift/Alt give both clicks and Enter their own browser behavior on a link — new tab,
 * new window, download — so the row must never intercept an activation carrying one.
 */
function hasModifierKey(activation: MouseEvent<HTMLAnchorElement> | KeyboardEvent<HTMLAnchorElement>) {
  return activation.metaKey || activation.altKey || activation.ctrlKey || activation.shiftKey
}

/**
 * Mirrors react-router's internal `shouldProcessLinkClick`: only a plain left click may be
 * intercepted, so modified clicks keep normal link behavior.
 */
function isPlainLeftClick(mouseEvent: MouseEvent<HTMLAnchorElement>) {
  return mouseEvent.button === 0 && !hasModifierKey(mouseEvent)
}

/**
 * `ListItem` delegates Enter to the title through a programmatic `anchorRef.current.click()`, and
 * that click carries no modifier state — so a modified Enter would reach `isPlainLeftClick` looking
 * plain. Keep only that case from bubbling to the delegation so the browser's own anchor activation
 * runs instead.
 */
function stopModifiedEnterFromListItemDelegation(keyboardEvent: KeyboardEvent<HTMLAnchorElement>) {
  if (keyboardEvent.key !== 'Enter' || !hasModifierKey(keyboardEvent)) return
  keyboardEvent.stopPropagation()
}

/** Rows carry the repository as a joined `"owner/repo"`; the issue viewer needs the parts separately. */
function toDashboardIssueIdentifier(
  repoNameWithOwner: string,
  number: number,
  globalId: string,
  permalink: string,
  isAccountIssue?: boolean,
): DashboardIssueIdentifier {
  const [owner = '', repo = ''] = repoNameWithOwner.split('/')
  return {
    owner,
    repo,
    number,
    globalId,
    permalink,
    ...(isAccountIssue ? {isAccountIssue: true} : {}),
  }
}

function renderCompactMetadataValue(value: ReactNode) {
  return value === null || value === undefined || value === '' ? (
    <span className={compactStyles.emptyPlaceholder}>&mdash;</span>
  ) : (
    value
  )
}

interface IssueListItemProps {
  as?: ElementType
  role?: string
  issue: EnrichedIssueListItem
  showUnreadBorder?: boolean
  isSelected?: boolean
  onSelect?: (isSelected: boolean, isRangeSelection?: boolean) => void
  onSelectRow?: () => void
  onNavigate?: () => void
  metadataContainerClassName?: string
  separateAgentAssignees?: boolean
  showRepository?: boolean
  onFilterSelect?: (type: FilterSelectType, value: string) => void
  /**
   * Invoked with a custom issue field's name and query value when its pill is
   * clicked, so the surrounding list can filter by `field.<name>:<value>`.
   * Parallels `onFilterSelect` but carries the extra field-name dimension the
   * built-in filter types don't need. When omitted the field pills are static.
   */
  onIssueFieldSelect?: (fieldName: string, queryValue: string) => void
  /**
   * When provided, clicking the issue title opens the issue in a side panel instead of
   * navigating. Modified clicks (new tab/window) and keyboard-with-modifier still follow
   * the `href` so the row keeps its normal link semantics. The clicked anchor is passed
   * back so the caller can restore focus to it when the panel closes.
   */
  onIssueSelect?: (identifier: DashboardIssueIdentifier, returnFocusElement: HTMLElement) => void
  /** Builds the query-preserving URL to filter the list by an issue type name. */
  getIssueTypeHref: (issueTypeName: string) => string
  /** Builds the query-preserving URL to filter the list to blocked issues. */
  getIssueBlockedHref?: () => string
  /** Builds the query-preserving URL to filter the list by this parent issue. */
  getParentIssueHref?: (repoNameWithOwner: string, number: number) => string
  /**
   * Optional client-router link component for the issue type token (SPA nav).
   * When omitted the token renders a plain anchor from `getIssueTypeHref`.
   */
  issueTypeLinkComponent?: ElementType
  /**
   * Current sort key. When it is `updated` the row appends a "· Updated <time>"
   * suffix, matching the legacy row's behavior under the recently-updated sort.
   */
  sortingItemSelected?: string
}

export function IssueListItem({
  as,
  role,
  issue,
  showUnreadBorder = true,
  isSelected,
  onSelect,
  onSelectRow,
  onNavigate,
  metadataContainerClassName,
  separateAgentAssignees = false,
  showRepository = true,
  onFilterSelect,
  onIssueFieldSelect,
  onIssueSelect,
  getIssueTypeHref,
  getIssueBlockedHref,
  issueTypeLinkComponent,
  sortingItemSelected,
  getParentIssueHref,
}: IssueListItemProps) {
  const {variant} = useListViewVariant()
  const isCompact = variant === 'compact'
  const compactAssigneeOverflowEnabled = isFeatureEnabled('issues_pulls_dashboard_assignee_overflow')
  const agentAssignees = separateAgentAssignees
    ? issue.assignees.filter(assignee => assignee.isAgent || assignee.isCopilot)
    : []
  const humanAssignees = separateAgentAssignees
    ? issue.assignees.filter(assignee => !assignee.isAgent && !assignee.isCopilot)
    : issue.assignees
  const stateIcon = getIssueStateIcon(issue.state, issue.stateReason, issue.status)
  const issueType = issue.issueType
  const milestone = issue.milestone
  // `isReadByCurrentUser` arrives with the deferred payload; treat rows as read
  // until it loads so the unread accent doesn't flash before enrichment.
  const isRead = issue.isReadByCurrentUser ?? true

  const reactionSortDisplay = getReactionSortDisplay(sortingItemSelected)
  const reactionCount = reactionSortDisplay
    ? reactionSortDisplay.content
      ? (issue.reactionGroups?.find(group => group.content === reactionSortDisplay.content)?.totalCount ?? 0)
      : (issue.reactionGroups?.reduce((sum, group) => sum + group.totalCount, 0) ?? 0)
    : 0

  const handleTitleClick =
    onIssueSelect || onSelectRow || onNavigate
      ? (event: MouseEvent<HTMLAnchorElement>) => {
          onSelectRow?.()

          // Preserve normal link behavior for modified/non-primary clicks (open in new tab, etc.).
          if (event.defaultPrevented || !isPlainLeftClick(event)) return

          onNavigate?.()
          if (!onIssueSelect) return

          const identifier = toDashboardIssueIdentifier(
            issue.repoNameWithOwner,
            issue.number,
            getListItemIdentity(issue),
            issue.permalink,
            issue.isAccountIssue,
          )

          event.preventDefault()
          // Pass the clicked anchor (event.currentTarget) so focus can return to it on close,
          // rather than relying on document.activeElement (mouse clicks don't move focus in
          // Safari/WebKit, which would restore focus to the wrong element).
          onIssueSelect(identifier, event.currentTarget)
        }
      : undefined

  // Mirror the legacy issue row phrasing: open issues read "<author> opened
  // <createdAt>", closed issues read "by <author> was closed <closedAt>".
  const activityTimestamp = issue.closed ? (issue.closedAt ?? issue.createdAt) : issue.createdAt
  // `getSortFromQuery` returns the full sort value including direction (e.g.
  // `updated-desc`), so strip the suffix before comparing to the `updated` field.
  const isUpdatedSort = sortingItemSelected?.replace(/-(asc|desc)$/, '') === 'updated'
  // Copilot-authored issues display "Copilot" rather than the bot login, matching the legacy row.
  const author = issue.author
  const isCopilot = author?.isCopilot ?? false
  const isAgent = author?.isAgent ?? false
  // Regular (non-agent, non-Copilot) bots don't get a hovercard on the legacy row,
  // so mirror that here to avoid a broken/empty hovercard for those actors.
  const isNonAgentBot = author?.typename === 'Bot' && !isCopilot && !isAgent
  const authorDisplayName = isCopilot ? 'Copilot' : (author?.login ?? '')
  // Copilot + non-Copilot agent bots share the copilot hovercard (both are
  // resolved by bot slug); regular users get the user hovercard; regular
  // non-agent bots get no hovercard, matching the legacy row's `isNonAgentBot`
  // skip. Uses the modern `hovercardAttributes('type', options)` API rather
  // than the deprecated `hovercardAttributesForActor` helper.
  const authorHovercardAttributes = (() => {
    if (!author || isNonAgentBot) return {}
    if (isCopilot || isAgent) return hovercardAttributes('copilot', {botSlug: author.login})
    return hovercardAttributes('user', {login: author.login})
  })()
  const authorProfileReference = author ? (
    <ProfileReference login={authorDisplayName} profileName={author.name} isAgent={isCopilot || isAgent} />
  ) : null
  // Filtering replaces profile navigation when the list can filter
  const authorLink = !author ? null : onFilterSelect ? (
    <button
      type="button"
      className={`${styles.filterLink} ${styles.authorLink} ${metadataLinkStyles.Link}`}
      onClick={() => onFilterSelect('author', author.login)}
      data-testid="author-filter-link"
      {...authorHovercardAttributes}
    >
      {/* Prefixes rather than overrides the name so `name_handle` display ("Mona Lisa (monalisa)") stays in it. */}
      <VisuallyHidden>{'Filter by author '}</VisuallyHidden>
      {authorProfileReference}
    </button>
  ) : (
    <Link
      inline
      href={author.resourcePath}
      className={clsx(styles.authorLink, metadataLinkStyles.Link)}
      {...authorHovercardAttributes}
    >
      {authorProfileReference}
    </Link>
  )

  const labelBadges = isCompact
    ? undefined
    : renderLabelBadges({
        labels: issue.labels,
        onSelect: onFilterSelect ? label => onFilterSelect('label', label) : undefined,
        filterLinkClassName: styles.filterLink,
      })

  // Custom issue field pills, delivered in the primary payload so they render on
  // first paint (parity with the Relay main-query fetch). Collapsed in compact
  // mode alongside labels.
  const fieldBadges = isCompact
    ? undefined
    : renderIssueFieldBadges({
        fieldValues: issue.issueFieldValues ?? [],
        onSelect: onIssueFieldSelect,
        filterLinkClassName: styles.filterLink,
      })

  // Legacy renders the sub-issues progress as a title trailing badge (next to
  // the labels), not as description text.
  const subIssues = issue.subIssuesSummary
  const subIssuesBadge =
    !isCompact && subIssues && subIssues.total > 0 ? (
      <ListItemTrailingBadge key="sub-issues-summary" title="sub-issues summary">
        <span data-testid="sub-issues-summary">
          <Token
            leadingVisual={() => (
              <ProgressCircle
                percentCompleted={subIssues.percentCompleted}
                size={14}
                svgClassName={styles.subIssuesProgressCircle}
              />
            )}
            text={`${subIssues.completed} / ${subIssues.total}`}
            {...(getParentIssueHref
              ? {
                  as: 'a',
                  href: getParentIssueHref(issue.repoNameWithOwner, issue.number),
                  // Keeps the visible "1 / 4" inside the name for voice control (WCAG 2.5.3).
                  'aria-label': `${subIssues.completed} / ${subIssues.total} sub-issues completed, filter by parent issue ${issue.repoNameWithOwner}#${issue.number}`,
                }
              : {})}
          />
        </span>
      </ListItemTrailingBadge>
    ) : null

  // Order mirrors the Relay row: sub-issues progress, then labels, then the
  // custom field pills (with their own `+N` overflow at the end).
  const allTrailingBadges = [
    ...(subIssuesBadge ? [subIssuesBadge] : []),
    ...(labelBadges ?? []),
    ...(fieldBadges ?? []),
  ]
  const trailingBadges = allTrailingBadges.length > 0 ? allTrailingBadges : undefined
  const compactAuthor =
    author &&
    (onFilterSelect ? (
      <button
        type="button"
        className={clsx(styles.filterLink, metadataLinkStyles.Link)}
        onClick={() => onFilterSelect('author', author.login)}
        aria-label={`Filter by author ${authorDisplayName}`}
      >
        {authorDisplayName}
      </button>
    ) : (
      <span>{authorDisplayName}</span>
    ))
  const linkedPullRequestMetadata =
    issue.linkedPullRequestCount && issue.linkedPullRequestCount > 0 ? (
      <LinkedPullRequests totalCount={issue.linkedPullRequestCount} pullRequests={issue.linkedPullRequests} />
    ) : null
  const compactIssueType = issueType ? (
    <IssueTypeToken
      name={issueType.name}
      color={issueType.color}
      getTooltipText={isTruncated => (isTruncated ? issueType.name : undefined)}
      {...(issueTypeLinkComponent
        ? {as: issueTypeLinkComponent, to: getIssueTypeHref(issueType.name)}
        : {href: getIssueTypeHref(issueType.name)})}
    />
  ) : null
  const metadata = isCompact ? (
    <>
      <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataUtility)}>
        {renderCompactMetadataValue(linkedPullRequestMetadata)}
      </ListItemMetadata>
      <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataUtility)}>
        {renderCompactMetadataValue(
          issue.commentCount > 0 ? (
            <>
              <CommentIcon size={16} aria-label={`${issue.commentCount} comments`} /> {issue.commentCount}
            </>
          ) : null,
        )}
      </ListItemMetadata>
      {reactionSortDisplay ? (
        <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataUtility)}>
          {renderCompactMetadataValue(
            reactionCount > 0 ? (
              <>
                <span
                  role="img"
                  aria-label={`${reactionCount} ${reactionSortDisplay.label} reaction${reactionCount === 1 ? '' : 's'}`}
                >
                  {reactionSortDisplay.emoji ?? <SmileyIcon aria-hidden="true" />}
                </span>{' '}
                <span aria-hidden="true">{reactionCount}</span>
              </>
            ) : null,
          )}
        </ListItemMetadata>
      ) : null}
      {separateAgentAssignees ? (
        <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataAssignees)}>
          {renderCompactMetadataValue(
            agentAssignees.length > 0 ? (
              <AssigneeAvatars
                assignees={agentAssignees}
                onSelect={onFilterSelect ? filterValue => onFilterSelect('assignee', filterValue) : undefined}
                filterLinkClassName={styles.assigneeFilterButton}
                maxVisible={compactAssigneeOverflowEnabled ? 2 : undefined}
              />
            ) : null,
          )}
        </ListItemMetadata>
      ) : null}
      <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataAssignees)}>
        {renderCompactMetadataValue(
          humanAssignees.length > 0 ? (
            <AssigneeAvatars
              assignees={humanAssignees}
              onSelect={onFilterSelect ? filterValue => onFilterSelect('assignee', filterValue) : undefined}
              filterLinkClassName={styles.assigneeFilterButton}
              maxVisible={compactAssigneeOverflowEnabled ? 2 : undefined}
            />
          ) : null,
        )}
      </ListItemMetadata>
    </>
  ) : (
    <>
      <ListItemMetadata className={styles.metadataCell}>{linkedPullRequestMetadata}</ListItemMetadata>
      <ListItemMetadata className={styles.metadataCell}>
        {issue.commentCount > 0 ? (
          <>
            <CommentIcon size={16} aria-label={`${issue.commentCount} comments`} /> {issue.commentCount}
          </>
        ) : null}
      </ListItemMetadata>
      {reactionSortDisplay ? (
        <ListItemMetadata className={styles.metadataCell}>
          {reactionCount > 0 ? (
            <>
              <span
                role="img"
                aria-label={`${reactionCount} ${reactionSortDisplay.label} reaction${reactionCount === 1 ? '' : 's'}`}
              >
                {reactionSortDisplay.emoji ?? <SmileyIcon aria-hidden="true" />}
              </span>{' '}
              <span aria-hidden="true">{reactionCount}</span>
            </>
          ) : null}
        </ListItemMetadata>
      ) : null}
      {separateAgentAssignees ? (
        <ListItemMetadata className={styles.metadataCell} alignment="right">
          {agentAssignees.length > 0 ? (
            <AssigneeAvatars
              assignees={agentAssignees}
              onSelect={onFilterSelect ? filterValue => onFilterSelect('assignee', filterValue) : undefined}
              filterLinkClassName={styles.assigneeFilterButton}
            />
          ) : null}
        </ListItemMetadata>
      ) : null}
      <ListItemMetadata className={styles.metadataCell} alignment="right">
        {humanAssignees.length > 0 ? (
          <AssigneeAvatars
            assignees={humanAssignees}
            onSelect={onFilterSelect ? filterValue => onFilterSelect('assignee', filterValue) : undefined}
            filterLinkClassName={styles.assigneeFilterButton}
            alignRight
          />
        ) : null}
      </ListItemMetadata>
    </>
  )

  return (
    <ListItem
      as={as}
      role={role}
      key={issue.id}
      isSelected={isSelected}
      onSelect={onSelect}
      className={unreadRowClassName(!showUnreadBorder || isRead)}
      title={
        <ListItemTitle
          href={issue.permalink}
          onClick={handleTitleClick}
          onKeyDown={stopModifiedEnterFromListItemDelegation}
          value={issue.titleHtml ? <VerifiedHTMLText as="span" html={issue.titleHtml} /> : <span>{issue.title}</span>}
          linkProps={{'data-testid': 'issue-listitem-title-link'}}
          anchorClassName={inlineCodeStyles.inlineCodeTitle}
          trailingBadges={trailingBadges}
        />
      }
      metadata={metadata}
      metadataContainerClassName={clsx(
        styles.metadataContainer,
        isCompact && compactStyles.metadataContainer,
        metadataContainerClassName,
      )}
    >
      <ListItemLeadingContent>
        <UnreadRowLabel isRead={isRead} />
        <ListItemLeadingVisual>{stateIcon}</ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent>
        {isCompact ? (
          <ListItemDescription className={clsx(compactStyles.leftMetadata, styles.compactDescription)}>
            <span className={compactStyles.leftMetadataText}>
              <IssueReference compact issue={issue} onFilterSelect={onFilterSelect} showRepository={showRepository} />
            </span>
            {compactAuthor ? (
              <>
                <span className={compactStyles.leftMetadataSeparator} aria-hidden="true">
                  •
                </span>
                <span className={compactStyles.leftMetadataText}>{compactAuthor}</span>
              </>
            ) : null}
            {compactIssueType ? <span className={compactStyles.leftMetadataText}>{compactIssueType}</span> : null}
          </ListItemDescription>
        ) : (
          <ListItemDescription>
            {issueType && (
              <span className="d-inline-block">
                <IssueTypeToken
                  name={issueType.name}
                  color={issueType.color}
                  getTooltipText={isTruncated => (isTruncated ? issueType.name : undefined)}
                  {...(issueTypeLinkComponent
                    ? {as: issueTypeLinkComponent, to: getIssueTypeHref(issueType.name)}
                    : {href: getIssueTypeHref(issueType.name)})}
                />
              </span>
            )}
            {!issue.closed && issue.blockedByCount != null && issue.blockedByCount > 0 && (
              <Token
                data-testid="blocked-by"
                text="Blocked"
                leadingVisual={() => <BlockedIcon className="fgColor-danger" size={14} />}
                className={styles.blockedToken}
                {...(getIssueBlockedHref
                  ? {as: 'a', href: getIssueBlockedHref(), 'aria-label': 'Blocked, filter by blocked issues'}
                  : {})}
              />
            )}
            <IssueReference issue={issue} showRepository={showRepository} />
            {author && (
              <span>
                {' · '}
                {issue.closed && <span>by </span>}
                {authorLink} <span>{issue.closed ? 'was closed' : 'opened'}</span>{' '}
                <IssueRelativeTime data-testid="issue-activity-timestamp" datetime={activityTimestamp} />
              </span>
            )}
            {isUpdatedSort && (
              <span data-testid="updated-at">
                {' · '}Updated <IssueRelativeTime datetime={issue.updatedAt} />
              </span>
            )}
            {/* Legacy renders a text label only for duplicates, since the duplicate and
                  not-planned state icons share the same muted skip glyph. */}
            {issue.stateReason === 'DUPLICATE' && <span data-testid="state-reason"> · Duplicate</span>}
            {milestone && (
              <span>
                {' · '}
                <Link
                  href={milestone.url}
                  inline
                  muted
                  className={metadataLinkStyles.Link}
                  aria-label={`Milestone: ${milestone.title}`}
                >
                  <MilestoneIcon size={16} /> {milestone.title}
                </Link>
              </span>
            )}
          </ListItemDescription>
        )}
      </ListItemMainContent>
    </ListItem>
  )
}
